import { useReducer, useMemo } from 'react';

export interface NigoAnalysisState {
  type: 'loading' | 'idle' | 'analyzing' | 'saving' | 'error';
  results: {
    aiResult: ParsedNigoResponse | null;
    partialAiResult: ParsedNigoResponse | null;
    ruleResult: RuleResult[];
    resolvedIssues: RuleResult[];
  };
  meta: {
    lastRunTimestamp: Date | null;
    stage: '70' | '100';
  };
  ui: {
    isPartialLoading: boolean;
    isResultLoading: boolean;
    error: string | null;
  };
}

export type NigoAnalysisAction =
  | {
      type: 'LOAD_FROM_DB';
      payload: {
        ruleResult?: RuleResult[];
        aiResult?: ParsedNigoResponse | null;
        resolvedIssues?: RuleResult[];
        lastRunAt?: Date;
      };
    }
  | { type: 'START_ANALYSIS'; payload: { stage: '70' | '100' } }
  | {
      type: 'SET_AI_RESULT';
      payload: ParsedNigoResponse;
      isPartial: boolean;
    }
  | {
      type: 'SET_RULE_RESULT';
      payload: {
        ruleResult: RuleResult[];
        resolvedIssues: RuleResult[];
      };
    }
  | { type: 'UPDATE_RESOLVED_ISSUES'; payload: RuleResult[] }
  | { type: 'ANALYSIS_COMPLETE' }
  | { type: 'ANALYSIS_ERROR'; error: string }
  | { type: 'START_SAVING' }
  | { type: 'SAVE_SUCCESS' }
  | { type: 'SAVE_ERROR'; error: string }
  | { type: 'CLEAR_ERROR' };

const initialState: NigoAnalysisState = {
  type: 'loading',
  results: {
    aiResult: null,
    partialAiResult: null,
    ruleResult: [],
    resolvedIssues: [],
  },
  meta: {
    lastRunTimestamp: null,
    stage: '70',
  },
  ui: {
    isPartialLoading: false,
    isResultLoading: false,
    error: null,
  },
};

function nigoAnalysisReducer(
  state: NigoAnalysisState,
  action: NigoAnalysisAction,
): NigoAnalysisState {
  switch (action.type) {
    case 'LOAD_FROM_DB': {
      const { ruleResult, aiResult, resolvedIssues, lastRunAt } = action.payload;

      return {
        ...state,
        type: 'idle',
        results: {
          ...state.results,
          ruleResult: ruleResult || state.results.ruleResult,
          aiResult: aiResult !== undefined ? aiResult : state.results.aiResult,
          resolvedIssues: resolvedIssues || state.results.resolvedIssues,
        },
        meta: {
          ...state.meta,
          lastRunTimestamp: lastRunAt || state.meta.lastRunTimestamp,
        },
      };
    }

    case 'START_ANALYSIS': {
      const isPartial = action.payload.stage === '70';
      return {
        ...state,
        type: 'analyzing',
        meta: {
          ...state.meta,
          stage: action.payload.stage,
        },
        ui: {
          ...state.ui,
          isPartialLoading: isPartial,
          isResultLoading: !isPartial,
          error: null,
        },
      };
    }

    case 'SET_AI_RESULT': {
      const now = new Date();

      return {
        ...state,
        results: {
          ...state.results,
          ...(action.isPartial
            ? { partialAiResult: action.payload }
            : { aiResult: action.payload }),
        },
        meta: {
          ...state.meta,
          lastRunTimestamp: now,
        },
        ui: {
          ...state.ui,
          ...(action.isPartial ? { isPartialLoading: false } : { isResultLoading: false }),
          error: null,
        },
      };
    }

    case 'SET_RULE_RESULT': {
      const now = new Date();

      return {
        ...state,
        results: {
          ...state.results,
          ruleResult: action.payload.ruleResult,
          resolvedIssues: action.payload.resolvedIssues,
        },
        meta: {
          ...state.meta,
          lastRunTimestamp: now,
        },
      };
    }

    case 'UPDATE_RESOLVED_ISSUES': {
      return {
        ...state,
        results: {
          ...state.results,
          resolvedIssues: action.payload,
        },
      };
    }

    case 'ANALYSIS_COMPLETE': {
      return {
        ...state,
        type: 'idle',
        ui: {
          ...state.ui,
          isPartialLoading: false,
          isResultLoading: false,
        },
      };
    }

    case 'ANALYSIS_ERROR': {
      return {
        ...state,
        type: 'error',
        ui: {
          ...state.ui,
          isPartialLoading: false,
          isResultLoading: false,
          error: action.error,
        },
      };
    }

    case 'START_SAVING': {
      return {
        ...state,
        type: 'saving',
      };
    }

    case 'SAVE_SUCCESS': {
      return {
        ...state,
        type: 'idle',
      };
    }

    case 'SAVE_ERROR': {
      return {
        ...state,
        type: 'error',
        ui: {
          ...state.ui,
          error: action.error,
        },
      };
    }

    case 'CLEAR_ERROR': {
      return {
        ...state,
        type: 'idle',
        ui: {
          ...state.ui,
          error: null,
        },
      };
    }

    default:
      return state;
  }
}

export function useNigoAnalysisStateMachine() {
  const [state, dispatch] = useReducer(nigoAnalysisReducer, initialState);

  const actions = useMemo(
    () => ({
      loadFromDB: (payload: {
        ruleResult?: RuleResult[];
        aiResult?: ParsedNigoResponse | null;
        resolvedIssues?: RuleResult[];
        lastRunAt?: Date;
      }) => dispatch({ type: 'LOAD_FROM_DB', payload }),

      startAnalysis: (stage: '70' | '100') =>
        dispatch({ type: 'START_ANALYSIS', payload: { stage } }),

      setAiResult: (payload: ParsedNigoResponse, isPartial: boolean) =>
        dispatch({ type: 'SET_AI_RESULT', payload, isPartial }),

      setRuleResult: (ruleResult: RuleResult[], resolvedIssues: RuleResult[]) =>
        dispatch({ type: 'SET_RULE_RESULT', payload: { ruleResult, resolvedIssues } }),

      updateResolvedIssues: (resolvedIssues: RuleResult[]) =>
        dispatch({ type: 'UPDATE_RESOLVED_ISSUES', payload: resolvedIssues }),

      analysisComplete: () => dispatch({ type: 'ANALYSIS_COMPLETE' }),

      analysisError: (error: string) => dispatch({ type: 'ANALYSIS_ERROR', error }),

      startSaving: () => dispatch({ type: 'START_SAVING' }),

      saveSuccess: () => dispatch({ type: 'SAVE_SUCCESS' }),

      saveError: (error: string) => dispatch({ type: 'SAVE_ERROR', error }),

      clearError: () => dispatch({ type: 'CLEAR_ERROR' }),
    }),
    [],
  );

  const computed = useMemo(
    () => ({
      isLoading: state.ui.isPartialLoading || state.ui.isResultLoading,
      hasAiResult: state.results.aiResult !== null || state.results.partialAiResult !== null,
      hasRuleResult: state.results.ruleResult.length > 0,
      hasResolvedIssues: state.results.resolvedIssues.length > 0,
      currentAiResult: state.results.aiResult || state.results.partialAiResult,
    }),
    [
      state.ui.isPartialLoading,
      state.ui.isResultLoading,
      state.results.aiResult,
      state.results.partialAiResult,
      state.results.ruleResult,
      state.results.resolvedIssues,
    ],
  );

  return {
    state,
    actions,
    computed,
  };
}
